import React, { useMemo } from 'react';
import {
  Box, Typography, Button, Grid,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { useHubPetParent } from './HubPetParentContext';
import HubSectionContainer from './CustomContainer';
import PhotoCard from './PhotoCard';
import MediaDialog from '../MediaDialog';
import PhotoLoadingGrid from './PhotoLoadingGrid';
import PhotoEmptyState from './PhotoEmptyState';
import usePhotoValidation from './usePhotoValidation';
import { photoPetLabel } from './petLabel';
import useMediaDialog from './useMediaDialog';
import { PHOTOS_PER_PAGE } from './photoUtils';
import { GET_PET_PHOTOS } from '../../queries';
import { PetPhotosResponse } from '../../types/PetPhoto';
import useHubPageView from './useHubPageView';

function MyPhotos() {
  useHubPageView();
  const { dialogState, openMediaDialog, setDialogOpen } = useMediaDialog();

  const { petParent, loading: userLoading } = useHubPetParent();
  const petParentId = petParent?.id;

  const {
    data,
    loading: photosLoading,
    fetchMore,
  } = useQuery<{ petPhotos: PetPhotosResponse }>(GET_PET_PHOTOS, {
    variables: {
      petParentId: petParentId ? parseInt(petParentId, 10) : null,
      limit: PHOTOS_PER_PAGE,
      offset: 0,
    },
    skip: !petParentId,
    fetchPolicy: 'cache-and-network', // Show cached data immediately while fetching fresh data
  });

  const photos = useMemo(() => data?.petPhotos?.photos || [], [data?.petPhotos?.photos]);
  const hasMore = data?.petPhotos?.hasMore || false;

  const { validPhotos, isValidating } = usePhotoValidation(photos);
  // Only show full-page loading on initial load (no data yet or loading with no photos).
  // When fetching more, keep showing existing content.
  // Also show loading while validating if we don't have valid photos yet.
  // Or if we have photos but no valid photos (validation pending).
  const isInitialLoading = userLoading
    || (!data && photosLoading)
    || (photosLoading && photos.length === 0)
    || (isValidating && validPhotos.length === 0)
    || (photos.length > 0 && validPhotos.length === 0); // Photos exist but validation not complete
  const isFetchingMore = photosLoading && photos.length > 0;

  const handleLoadMore = () => {
    fetchMore({
      variables: {
        petParentId: petParentId ? parseInt(petParentId, 10) : null,
        offset: photos.length,
        limit: PHOTOS_PER_PAGE,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          petPhotos: {
            photos: [
              ...prev.petPhotos.photos,
              ...fetchMoreResult.petPhotos.photos,
            ],
            hasMore: fetchMoreResult.petPhotos.hasMore,
          },
        };
      },
    });
  };

  return (
    <>
      <Box sx={{ borderBottom: '1px solid #E0E0E0' }}>
        <HubSectionContainer wide sx={{ gap: 0.5, py: 3 }}>
          <Typography variant="sectionTitle">My Photos</Typography>
          <Typography variant="body2">
            Photos you&apos;ve shared with Petcademy, visible to other pet parents in the Petcademy community.
          </Typography>
        </HubSectionContainer>
      </Box>

      <Box sx={{ background: '#F6F5FA', pb: 4, flexGrow: 1 }}>
        <HubSectionContainer wide backgroundContrast sx={{ py: 3 }}>
          {isInitialLoading && <PhotoLoadingGrid />}

          {!isInitialLoading && !isValidating && photos.length === 0 && validPhotos.length === 0 && (
            <PhotoEmptyState
              caption="Your photo feed will start filling up as you share moments with your pet."
            />
          )}

          {!isInitialLoading && validPhotos.length > 0 && (
            <>
              <Grid container spacing={2}>
                {validPhotos.map((photo) => (
                  <Grid item xs={12} sm={4} md={3} key={photo.id}>
                    <PhotoCard
                      photo={photo}
                      onImageClick={() => openMediaDialog(photo.url, photo.createdAt, photoPetLabel(photo))}
                    />
                  </Grid>
                ))}
              </Grid>

              {hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="text"
                    onClick={handleLoadMore}
                    disabled={isFetchingMore}
                  >
                    {isFetchingMore ? 'Loading...' : 'Show More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </HubSectionContainer>
      </Box>
      <MediaDialog
        dialogImg={dialogState.img}
        dialogDate={dialogState.date}
        imgDialogOpen={dialogState.open}
        setImgDialogOpen={setDialogOpen}
        petName={dialogState.petName}
      />
    </>
  );
}

export default MyPhotos;
